/** Maximum studio page file size (512 MiB). Client multipart uploads bypass serverless body limits. */
export const STUDIO_UPLOAD_MAX_BYTES = 512 * 1024 * 1024;

/** Use multipart client uploads for files larger than 4 MiB. */
export const STUDIO_MULTIPART_MIN_BYTES = 4 * 1024 * 1024;

export function isLocalDevHostname(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]"
  );
}

/** Multipart uses vercel.com/api/blob/mpu from the browser, which blocks localhost CORS. */
export function shouldUseStudioMultipartUpload(
  fileSize: number,
  hostname?: string,
): boolean {
  if (hostname && isLocalDevHostname(hostname)) {
    return false;
  }
  return fileSize > STUDIO_MULTIPART_MIN_BYTES;
}

export const STUDIO_UPLOAD_ALLOWED_EXTENSIONS = new Set([
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
  "txt",
  "csv",
  "zip",
]);

export const STUDIO_UPLOAD_ALLOWED_CONTENT_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
  "text/csv",
  "application/zip",
  "application/x-zip-compressed",
];

/** Maximum OG image upload size (700 KiB). */
export const STUDIO_OG_IMAGE_MAX_BYTES = 700 * 1024;

export const STUDIO_OG_IMAGE_ALLOWED_EXTENSIONS = new Set(["png", "webp"]);

export const STUDIO_OG_IMAGE_ALLOWED_CONTENT_TYPES = [
  "image/png",
  "image/webp",
];

/** Maximum Studio content image upload size (5 MiB). */
export const STUDIO_CONTENT_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

/** Soft size bands for pre-upload quality reporting (bytes). */
export const STUDIO_CONTENT_IMAGE_SIZE_GREEN_MAX_BYTES = 200 * 1024;
export const STUDIO_CONTENT_IMAGE_SIZE_YELLOW_MAX_BYTES = 600 * 1024;

export const STUDIO_CONTENT_IMAGE_ALLOWED_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "png",
  "webp",
]);

export const STUDIO_CONTENT_IMAGE_ALLOWED_CONTENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
];

export const STUDIO_CONTENT_IMAGE_PREFERRED_EXTENSIONS = new Set([
  "png",
  "webp",
]);

export type StudioUploadPurpose = "page-file" | "og-image" | "content-image";

const UNSAFE_SEGMENT_CHARS = /[^a-zA-Z0-9._-]/g;

export function sanitizeSegment(value: string, maxLen = 80): string {
  return value.trim().replaceAll(UNSAFE_SEGMENT_CHARS, "-").slice(0, maxLen);
}

export function sanitizeFilename(name: string, maxLen = 120): string {
  const basename = name.split(/[/\\]/).pop() ?? name;
  const sanitized = basename
    .replaceAll(UNSAFE_SEGMENT_CHARS, "-")
    .slice(0, maxLen);
  return sanitized || "file";
}

/** Lower-cased, sanitized file extension (without the dot), or "" when absent. */
export function fileExtension(name: string): string {
  const dot = name.lastIndexOf(".");
  if (dot <= 0 || dot === name.length - 1) return "";
  return name
    .slice(dot + 1)
    .toLowerCase()
    .replaceAll(UNSAFE_SEGMENT_CHARS, "");
}

export function isAllowedStudioUploadExtension(ext: string): boolean {
  return STUDIO_UPLOAD_ALLOWED_EXTENSIONS.has(ext.toLowerCase());
}

export function isAllowedStudioOgImageExtension(ext: string): boolean {
  return STUDIO_OG_IMAGE_ALLOWED_EXTENSIONS.has(ext.toLowerCase());
}

export function isAllowedStudioContentImageExtension(ext: string): boolean {
  return STUDIO_CONTENT_IMAGE_ALLOWED_EXTENSIONS.has(ext.toLowerCase());
}

export function parseStudioUploadPurpose(
  value: unknown,
): StudioUploadPurpose {
  if (value === "og-image" || value === "content-image") {
    return value;
  }
  return "page-file";
}

export function buildStudioBlobPath(input: {
  pageId: string;
  resourceId: string;
  filename: string;
}): string {
  const pageId = sanitizeSegment(input.pageId) || "shared";
  const resourceId = sanitizeSegment(input.resourceId, 40);
  const filename = sanitizeFilename(input.filename);
  return `studio/${pageId}/${resourceId}/${filename}`;
}
